import { useState, useCallback, useRef, useEffect } from 'react';
import { safeJsonParse } from '../../../utils/security';

export interface HeaderPair {
  key: string;
  value: string;
}

interface UseHeaderBuilderOptions {
  watch: (field: string) => unknown;
  setValue: (field: string, value: unknown, options?: Record<string, boolean>) => void;
  clearErrors: (field: string) => void;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isHeaderPair(value: HeaderPair | null): value is HeaderPair {
  return value !== null && value.key.trim().length > 0;
}

function toHeaderPairs(data: Record<string, unknown>): HeaderPair[] {
  const entries = Object.entries(data).map(([key, value]) => ({
    key,
    value: String(value),
  }));

  return entries.length > 0 ? entries : [{ key: '', value: '' }];
}

function splitTopLevelEntries(input: string): string[] {
  const entries: string[] = [];
  let current = '';
  let inQuotes = false;
  let escaped = false;

  for (const char of input) {
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }

    if (char === '\\') {
      current += char;
      escaped = true;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      current += char;
      continue;
    }

    if (char === ',' && !inQuotes) {
      if (current.trim()) {
        entries.push(current.trim());
      }
      current = '';
      continue;
    }

    current += char;
  }

  if (current.trim()) {
    entries.push(current.trim());
  }

  return entries;
}

function findTopLevelColon(input: string): number {
  let inQuotes = false;
  let escaped = false;

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (char === '\\') {
      escaped = true;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === ':' && !inQuotes) {
      return i;
    }
  }

  return -1;
}

function decodeJsonString(value: string): string {
  try {
    return JSON.parse(value);
  } catch {
    return value.slice(1, -1);
  }
}

function parseLooseHeaderPairs(json: string): HeaderPair[] | null {
  const trimmed = json.trim();
  if (!trimmed || trimmed === '{}') {
    return [{ key: '', value: '' }];
  }

  const withoutBraces = trimmed
    .replace(/^\s*\{/, '')
    .replace(/\}\s*$/, '')
    .trim();

  if (!withoutBraces) {
    return [{ key: '', value: '' }];
  }

  const parsedPairs = splitTopLevelEntries(withoutBraces)
    .map((entry) => {
      const colonIndex = findTopLevelColon(entry);
      if (colonIndex === -1) {
        return null;
      }

      const rawKey = entry.slice(0, colonIndex).trim();
      const rawValue = entry.slice(colonIndex + 1).trim();

      if (!rawKey) {
        return null;
      }

      const key = rawKey.startsWith('"') && rawKey.endsWith('"')
        ? decodeJsonString(rawKey)
        : rawKey;

      const value = rawValue.startsWith('"') && rawValue.endsWith('"')
        ? decodeJsonString(rawValue)
        : rawValue;

      return { key, value };
    })
    .filter(isHeaderPair);

  return parsedPairs.length > 0 ? parsedPairs : null;
}

export function useHeaderBuilder({ watch, setValue, clearErrors }: UseHeaderBuilderOptions) {
  const [pairs, setPairsState] = useState<HeaderPair[]>([]);
  const hydrated = useRef(false);

  // Hydrate from headerMap JSON on mount
  useEffect(() => {
    if (hydrated.current) return;
    const raw = (watch('headerMap') as string) || '{}';
    const parsed = safeJsonParse(raw, {});
    if (parsed.success && isObjectRecord(parsed.data)) {
      setPairsState(toHeaderPairs(parsed.data));
    } else {
      const loosePairs = parseLooseHeaderPairs(raw);
      if (loosePairs) {
        setPairsState(loosePairs);
      }
    }
    hydrated.current = true;
  }, []);

  // Sync pairs → headerMap JSON
  const syncToForm = useCallback(
    (updated: HeaderPair[]) => {
      setPairsState(updated);
      const obj: Record<string, string> = {};
      for (const p of updated) {
        if (p.key.trim()) obj[p.key] = p.value;
      }
      clearErrors('headerMap');
      setValue('headerMap', JSON.stringify(obj, null, 2), { shouldDirty: true });
    },
    [setValue, clearErrors]
  );

  const updatePair = useCallback(
    (idx: number, field: keyof HeaderPair, value: string) => {
      const updated = pairs.map((p, i) =>
        i === idx ? { ...p, [field]: value } : p
      );
      syncToForm(updated);
    },
    [pairs, syncToForm]
  );

  const addPair = useCallback(() => {
    syncToForm([...pairs, { key: '', value: '' }]);
  }, [pairs, syncToForm]);

  const removePair = useCallback(
    (idx: number) => {
      const updated = pairs.filter((_, i) => i !== idx);
      syncToForm(updated.length > 0 ? updated : [{ key: '', value: '' }]);
    },
    [pairs, syncToForm]
  );

  /** Re-hydrate key-value pairs from a raw JSON string (call when the JSON editor changes) */
  const hydrateFromJson = useCallback((json: string) => {
    const normalizedJson = json.trim() === '""' || json.trim() === '' ? '{}' : json;
    // Strip trailing commas before closing braces/brackets to tolerate common editing artifacts
    const sanitized = normalizedJson.replace(/,\s*([\]}])/g, '$1');
    const parsed = safeJsonParse(sanitized, {});
    if (parsed.success && isObjectRecord(parsed.data)) {
      setPairsState(toHeaderPairs(parsed.data));
      return;
    }

    const loosePairs = parseLooseHeaderPairs(sanitized);
    if (loosePairs) {
      setPairsState(loosePairs);
    }
  }, []);

  return {
    pairs,
    updatePair,
    addPair,
    removePair,
    hydrateFromJson,
  };
}
